// Themes studio: a ThemeSpec (kinds/themeprofile/schema.ts) to what the Shell sets. Pure.
// The spec is typed (hex colours, ranged numbers, closed enums), so every string this
// returns is built from validated parts and never from free text. An empty spec comes to
// exactly the variables the same device mode gets without a studio (byte-identical),
// so a look with no `studio` and a look with an empty one draw the same.
import {
  DANGER,
  GLASS,
  STATE_COLOURS,
  THEME_DEFAULT_ACCENT,
  accentVarsFor,
  horizonVars,
  mixHex,
  onAccentFor,
  themeVars,
  type Hue,
  type PaletteRecord,
  type Theme,
} from '@/lib/colour'
import {
  COLOUR_RE,
  THEME_SPEC_V,
  themeSpec,
  type AccentSpec,
  type Background,
  type ModeSpec,
  type StudioMode,
  type SurfacesSpec,
  type ThemeSpec,
} from '@/kinds/themeprofile/schema'
import type { Resolved } from '@/lib/horizon'

export type { StudioMode, ThemeSpec }

/** What the studio draws behind the page; `none` leaves the surface alone. */
export type StudioBackground =
  | { kind: 'none' }
  | { kind: 'gradient'; css: string }
  | { kind: 'image'; blob: string; fit: 'cover' | 'contain'; dim: number; blur: number }
  | { kind: 'backdrop'; id: string }

export interface StudioDraw {
  vars: Record<string, string>
  /** attributes for <html>: data-motion, data-space, data-type; absent means unset */
  attrs: { motion?: 'reduced' | 'none'; space?: '1'; type?: '1' }
  background: StudioBackground
  /** the spec asks for glass and the mode can show it (dark or black) */
  glass: boolean
}

/** The studio mode a device's resolved appearance draws with. */
export const studioModeOf = (resolved: Resolved): StudioMode =>
  resolved === 'day' ? 'light' : resolved === 'night' ? 'dark' : 'black'

const horizonOf: Record<StudioMode, 'day' | 'night' | 'black'> = {
  light: 'day',
  dark: 'night',
  black: 'black',
}

/** Can this build draw the spec? A newer version is kept as given and not drawn. */
export const canDraw = (spec: unknown): spec is ThemeSpec =>
  typeof spec === 'object' &&
  spec !== null &&
  (spec as { v?: unknown }).v === THEME_SPEC_V &&
  themeSpec.safeParse(spec).success

const ok = (c: string | undefined): c is string => !!c && COLOUR_RE.test(c)
const px = (n: number) => `${Math.round(n * 100) / 100}px`
const num = (n: number) => `${Math.round(n * 1000) / 1000}`

const BASE_LIGHT: Partial<Record<Theme, true>> = { paper: true }

/** The shape's radius as the nested pairs (inner = outer - padding), scaled from the 16px card. */
export const radiusVars = (r: number): Record<string, string> => {
  const f = r / 16
  const widget = Math.round(22 * f)
  const control = Math.round(12 * f)
  const sheet = Math.round(28 * f)
  const bubble = Math.round(18 * f)
  const composer = Math.round(24 * f)
  return {
    '--radius-card': px(r),
    '--radius-card-inner': px(Math.max(r - 6, 0)),
    '--radius-widget': px(widget),
    '--radius-control': px(control),
    '--radius-sheet': px(sheet),
    '--radius-sheet-inner': px(Math.max(sheet - 12, 0)),
    '--radius-bubble': px(bubble),
    '--radius-composer': px(composer),
    '--radius-composer-inner': px(Math.max(composer - 6, 0)),
  }
}

const merge = <T extends object>(a: T | undefined, b: T | undefined): T | undefined =>
  a || b ? ({ ...a, ...b } as T) : undefined

const backgroundOf = (b: Background | undefined): StudioBackground => {
  if (!b || b.kind === 'solid') return { kind: 'none' }
  if (b.kind === 'gradient') {
    const stops = [b.from, b.via, b.to].filter(ok).map((c) => c.toLowerCase())
    if (stops.length < 2) return { kind: 'none' }
    return { kind: 'gradient', css: `linear-gradient(${Math.round(b.angle ?? 180)}deg, ${stops.join(', ')})` }
  }
  if (b.kind === 'image')
    return { kind: 'image', blob: b.blob, fit: b.fit ?? 'cover', dim: b.dim ?? 0, blur: b.blur ?? 0 }
  return { kind: 'backdrop', id: b.id }
}

/**
 * The variables a spec sets for one mode, on top of the base the look already gives it.
 * `base` is what the Shell would set without a studio (horizonVars / themeVars + accent);
 * `look` supplies the palette and hue that base used.
 */
export const themeSpecToVars = (
  spec: ThemeSpec,
  mode: StudioMode,
  look?: { palette?: PaletteRecord; hue?: Hue },
): StudioDraw => {
  const light = mode === 'light'
  const black = mode === 'black'
  const m: ModeSpec = spec.modes?.[mode] ?? {}
  const surfaces: SurfacesSpec = m.surfaces ?? {}
  const accent: AccentSpec | undefined = black ? m.accent : merge(spec.accent, m.accent)
  const bg = m.background ?? (black ? undefined : spec.background)

  // the base: the studio's built-in theme where its lightness fits the mode, else the Horizon mode
  const baseTheme = spec.base
  const baseIsLight = !!baseTheme && !!BASE_LIGHT[baseTheme]
  const useTheme = !!baseTheme && !black && baseIsLight === light
  const hue = look?.hue ?? (baseTheme ? THEME_DEFAULT_ACCENT[baseTheme] : undefined)
  const glassOn = !!spec.glass?.on && !light
  const glassLook =
    glassOn && spec.glass
      ? {
          tint: 'suite' as const,
          glow: ((spec.glass.strength ?? 50) / 100) * GLASS.glowMax,
          blur: spec.glass.blur ?? GLASS.blur,
        }
      : undefined
  const vars: Record<string, string> = glassLook
    ? horizonVars('glass', look?.palette, look?.hue, glassLook)
    : useTheme
      ? { ...themeVars(baseTheme, undefined), ...accentVarsFor(undefined, hue, light) }
      : horizonVars(horizonOf[mode], look?.palette, look?.hue)
  if (useTheme) vars['--color-danger'] = light ? DANGER.light : DANGER.dark

  // surfaces by role
  const s = {
    ink: vars['--color-ink']!,
    panel: vars['--color-panel']!,
    line: vars['--color-line']!,
    fg: vars['--color-fg']!,
    muted: vars['--color-muted']!,
    faint: vars['--color-faint']!,
  }
  let bgc = ok(surfaces.background) ? surfaces.background : undefined
  if (black && bgc && m.trueBlack !== false) bgc = '#000000'
  if (bgc) vars['--color-ink'] = bgc
  if (ok(surfaces.raised)) {
    vars['--color-panel'] = surfaces.raised
    vars['--color-raised'] = mixHex(surfaces.raised, ok(surfaces.text) ? surfaces.text : s.fg, 0.08)
  }
  if (ok(surfaces.hairline)) {
    vars['--color-line'] = surfaces.hairline
    vars['--color-line-strong'] = mixHex(surfaces.hairline, ok(surfaces.text) ? surfaces.text : s.fg, 0.25)
  }
  if (ok(surfaces.text)) {
    vars['--color-fg'] = surfaces.text
    vars['--color-fg-2'] = mixHex(surfaces.text, ok(surfaces.muted) ? surfaces.muted : s.muted, 0.4)
  }
  if (ok(surfaces.muted)) {
    vars['--color-muted'] = surfaces.muted
    if (!ok(surfaces.text)) vars['--color-fg-2'] = mixHex(s.fg, surfaces.muted, 0.4)
  }
  if (ok(surfaces.faint)) vars['--color-faint'] = surfaces.faint
  if (ok(surfaces.sunken)) vars['--color-sunken'] = surfaces.sunken
  else if (bgc) vars['--color-sunken'] = bgc

  // accent, highlight and the states
  if (ok(accent?.colour)) {
    const a = accent.colour
    Object.assign(vars, accentVarsFor({ accent: a }, undefined, light))
    if (light) {
      vars['--color-accent-text'] = `color-mix(in oklab, ${a} 58%, black)`
      vars['--color-on-accent'] = onAccentFor(a, '#15172b') ?? '#15172b'
    }
  }
  if (ok(accent?.highlight)) {
    const h = accent.highlight
    vars['--color-highlight'] = h
    vars['--color-highlight-text'] = light
      ? `color-mix(in oklab, ${h} 58%, black)`
      : `color-mix(in oklab, ${h} 80%, white)`
  }
  const st = STATE_COLOURS[light ? 'light' : 'dark']
  for (const k of ['ok', 'warn', 'info'] as const) {
    if (ok(accent?.[k])) vars[`--color-${k}`] = accent[k]
    else if (spec.accent || m.accent || baseTheme) vars[`--color-${k}`] = st[k]
  }
  if (ok(accent?.danger)) vars['--color-danger'] = accent.danger

  // shape
  const attrs: StudioDraw['attrs'] = {}
  if (spec.shape?.radius !== undefined) Object.assign(vars, radiusVars(spec.shape.radius))
  if (spec.shape?.border !== undefined) vars['--studio-border'] = px(spec.shape.border)
  if (spec.shape?.hairline !== undefined) vars['--studio-hairline'] = px(spec.shape.hairline)

  // space: density and the multiplier become one scale, drawn on group rows and cards
  const density = spec.space?.density === 'compact' ? 0.88 : spec.space?.density === 'roomy' ? 1.12 : 1
  const scale = Math.round(density * (spec.space?.spacing ?? 1) * 1000) / 1000
  if (scale !== 1) {
    vars['--space-scale'] = num(scale)
    attrs.space = '1'
  }

  // type: the family stays Atkinson
  if (spec.type?.size !== undefined || spec.type?.weight !== undefined) {
    vars['--type-scale'] = num(spec.type?.size ?? 1)
    vars['--weight-body'] = String(spec.type?.weight ?? 400)
    attrs.type = '1'
  }

  if (spec.motion === 'reduced' || spec.motion === 'none') attrs.motion = spec.motion

  // app icon tint
  if (spec.icons) {
    const t = spec.icons.tint ?? 'none'
    const a = vars['--color-accent']!
    const tint =
      t === 'none'
        ? 'transparent'
        : t === 'accent'
          ? a
          : t === 'highlight'
            ? (vars['--color-highlight'] ?? a)
            : t
    vars['--icon-tint'] = tint
    vars['--icon-tint-strength'] = `${spec.icons.strength ?? 0}%`
  }

  const background = backgroundOf(bg)
  if (background.kind === 'gradient') vars['--studio-bg'] = background.css
  return { vars, attrs, background, glass: glassOn }
}
